import React from 'react'
import { Combobox } from '@CommonComponents'

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'custom', label: 'Custom…' },
]

// ISO date strings (YYYY-MM-DD) compare lexicographically the same as chronologically.
export const getDateErrors = values => {
  const { enrollment_start, enrollment_end, billing_date } = values
  let enrollmentError = null
  let billingDateError = null

  if (enrollment_start && enrollment_end && enrollment_start > enrollment_end) {
    enrollmentError = 'Enrollment start must be before enrollment end'
  }

  if (billing_date) {
    const compareDate = enrollment_end || enrollment_start
    if (compareDate && billing_date < compareDate) {
      billingDateError = enrollment_end
        ? 'Billing date must be on or after enrollment end'
        : 'Billing date must be on or after enrollment start'
    }
  }

  return { enrollmentError, billingDateError }
}

export const InlineBillingFields = ({
  schools,
  schoolAccounts,
  values,
  onChange,
  onSchoolChange,
  onOpenCreateModal,
  errors,
}) => {
  const {
    school_id,
    invoice_account_id,
    invoice_type,
    billing_price,
    billing_date,
    enrollment_start,
    enrollment_end,
    billing_frequency,
    billing_frequency_custom,
  } = values

  const { enrollmentError, billingDateError } = errors || {}
  const accounts = schoolAccounts || []

  const handleSchoolSelect = newSchoolId => {
    onChange('school_id', newSchoolId || '')
    onChange('invoice_account_id', '')
    onSchoolChange(newSchoolId || '')
  }

  const handleAccountChange = e => {
    const val = e.target.value
    if (val === '__new__') {
      onOpenCreateModal()
      return
    }
    onChange('invoice_account_id', val)
  }

  return (
    <div>
      {/* School + Invoice Account row */}
      <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '8px' }}>
        <div>
          <label className="small text-muted mb-1 d-block">School</label>
          <Combobox
            options={schools.map(s => ({ value: s.id, label: s.name }))}
            value={school_id || ''}
            onChange={handleSchoolSelect}
            placeholder="Select school…"
            className="combobox-school"
          />
        </div>

        <div className="align-self-end pb-1 text-muted">›</div>

        <div>
          <label className="small text-muted mb-1 d-block">Invoice Account</label>
          <select
            className="form-control form-control-sm"
            style={{ minWidth: '200px' }}
            value={invoice_account_id || ''}
            onChange={handleAccountChange}
            disabled={!school_id}
          >
            <option value="">Select account…</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
            <option value="__new__">+ New invoice account…</option>
          </select>
        </div>

        {/* Invoice type toggle */}
        <div>
          <label className="small text-muted mb-1 d-block">Invoice Type</label>
          <div className="btn-group btn-group-sm">
            <button
              type="button"
              className={`btn btn-myed ${invoice_type === 'one_time' ? 'btn-primary btn-primary-myed' : 'btn-default btn-default-myed'}`}
              onClick={() => onChange('invoice_type', 'one_time')}
            >
              One-time
            </button>
            <button
              type="button"
              className={`btn btn-myed ${invoice_type === 'recurring' ? 'btn-primary btn-primary-myed' : 'btn-default btn-default-myed'}`}
              onClick={() => onChange('invoice_type', 'recurring')}
            >
              Recurring
            </button>
          </div>
        </div>

        {/* Billing price */}
        <div>
          <label className="small text-muted mb-1 d-block">Billing Price</label>
          <div className="input-group input-group-sm" style={{ width: '110px' }}>
            <input
              type="number"
              className="form-control"
              min="0"
              step="0.01"
              value={billing_price || ''}
              onChange={e => onChange('billing_price', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* One-time conditional fields */}
      {invoice_type === 'one_time' && (
        <div>
          {/* Enrollment start / end row */}
          <div className="d-flex align-items-end flex-wrap mb-1" style={{ gap: '12px' }}>
            <div>
              <label className="small text-muted mb-1 d-block">Enrollment Start</label>
              <input
                type="date"
                className={`form-control form-control-sm${enrollmentError ? ' is-invalid' : ''}`}
                value={enrollment_start || ''}
                onChange={e => onChange('enrollment_start', e.target.value)}
              />
            </div>
            <div className="align-self-end pb-1 text-muted">–</div>
            <div>
              <label className="small text-muted mb-1 d-block">Enrollment End</label>
              <input
                type="date"
                className={`form-control form-control-sm${enrollmentError ? ' is-invalid' : ''}`}
                value={enrollment_end || ''}
                onChange={e => onChange('enrollment_end', e.target.value)}
              />
            </div>
          </div>
          {enrollmentError && (
            <p className="text-danger small mt-0 mb-2">{enrollmentError}</p>
          )}

          {/* Billing date row */}
          <div className="d-flex align-items-end flex-wrap mb-1" style={{ gap: '12px' }}>
            <div>
              <label className="small text-muted mb-1 d-block">Billing Date</label>
              <input
                type="date"
                className={`form-control form-control-sm${billingDateError ? ' is-invalid' : ''}`}
                value={billing_date || ''}
                onChange={e => onChange('billing_date', e.target.value)}
              />
              {billingDateError && (
                <p className="text-danger small mt-1 mb-0">{billingDateError}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recurring conditional fields */}
      {invoice_type === 'recurring' && (
        <div className="d-flex align-items-end flex-wrap mb-1" style={{ gap: '12px' }}>
          <div>
            <label className="small text-muted mb-1 d-block">Frequency</label>
            <select
              className="form-control form-control-sm"
              style={{ minWidth: '140px' }}
              value={billing_frequency || ''}
              onChange={e => onChange('billing_frequency', e.target.value)}
            >
              <option value="">Select…</option>
              {FREQUENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {billing_frequency === 'custom' && (
            <div>
              <label className="small text-muted mb-1 d-block">Describe</label>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ minWidth: '160px' }}
                placeholder="e.g. End of Semester"
                value={billing_frequency_custom || ''}
                onChange={e => onChange('billing_frequency_custom', e.target.value)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
